import { useEffect, useMemo, useRef, useState } from "react";
import { ActionList } from "../../components/ui";
import SortIcon from "../../components/ui/Sorting";
import SortSummary from "../../components/ui/SortSummary";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import Pagination from "../../components/ui/Pagination";
import { formatDateTime } from "../../helpers/formatDateTime";
import { getPendingExpiry } from "./pendingSites";
import type { PendingMode, PendingSite } from "./pendingSites";

const COLUMNS = [
    { key: "siteIdentifier", label: "Site Identifier" },
    { key: "siteName", label: "Site Name" },
    { key: "region", label: "Region" },
    { key: "systemTypeName", label: "System Type" },
    { key: "status", label: "Status" },
    { key: "createdAt", label: "Created" },
    { key: "expiresAt", label: "Expires" },
] as const;
type SortColumn = typeof COLUMNS[number]["key"];
const PAGE_SIZE = 50;

interface Props {
    sites: PendingSite[];
    loading: boolean;
    busy: boolean;
    selectedIds: Set<number>;
    onSelectionChange: (ids: Set<number>) => void;
    onAction: (site: PendingSite, mode: PendingMode) => void;
}

export function PendingSiteList({ sites, loading, busy, selectedIds, onSelectionChange, onAction }: Props) {
    const [sortColumn, setSortColumn] = useState<SortColumn>("createdAt");
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
    const [hasAppliedSort, setHasAppliedSort] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const headerRef = useRef<HTMLInputElement>(null);
    const activeColumn = COLUMNS.find(column => column.key === sortColumn);
    const sorted = useMemo(() => {
        const value = (site: PendingSite) => {
            if (sortColumn === "createdAt" || sortColumn === "expiresAt") {
                const date = sortColumn === "expiresAt" ? getPendingExpiry(site) : site.createdAt;
                return date ? new Date(date).getTime() : 0;
            }
            return (site[sortColumn] ?? "").toLowerCase();
        };
        return [...sites].sort((left, right) => {
            const first = value(left);
            const second = value(right);
            return (first < second ? -1 : first > second ? 1 : 0) * (sortOrder === "asc" ? 1 : -1);
        });
    }, [sites, sortColumn, sortOrder]);
    const totalPages = Math.max(1, Math.ceil(sites.length / PAGE_SIZE));
    const page = currentPage > totalPages ? 1 : currentPage;
    const paged = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    const allSelected = !!paged.length && paged.every(site => selectedIds.has(site.id));
    const someSelected = paged.some(site => selectedIds.has(site.id));
    useEffect(() => { if (currentPage > totalPages) setCurrentPage(1); }, [currentPage, totalPages]);
    useEffect(() => {
        if (headerRef.current) headerRef.current.indeterminate = someSelected && !allSelected;
    }, [someSelected, allSelected, loading]);
    const sort = (column: SortColumn) => {
        setHasAppliedSort(true);
        setCurrentPage(1);
        setSortOrder(previous => sortColumn === column && previous === "asc" ? "desc" : "asc");
        setSortColumn(column);
    };
    const reset = () => { setSortColumn("createdAt"); setSortOrder("desc"); setHasAppliedSort(false); setCurrentPage(1); };
    const toggle = (site: PendingSite) => {
        const next = new Set(selectedIds);
        if (next.has(site.id)) next.delete(site.id);
        else next.add(site.id);
        onSelectionChange(next);
    };
    const togglePage = () => {
        const next = new Set(selectedIds);
        paged.forEach(site => { if (allSelected) next.delete(site.id); else next.add(site.id); });
        onSelectionChange(next);
    };
    return (
        <>
            <div className="alarm-content--heading">
                <div>
                    <h3 className="table-title__alarms-list">Pending List ({paged.length} of {sites.length})</h3>
                    {hasAppliedSort && activeColumn && <SortSummary columnLabel={activeColumn.label} sortOrder={sortOrder}
                        onReset={reset} resetAriaLabel="Reset sorting to newest first" />}
                </div>
            </div>
            <div className="table-responsive">
                {loading ? <LoadingSpinner message="Loading pending sites…" /> : (
                    <table>
                        <thead><tr>
                            <th className="multiselect-input"><input type="checkbox" ref={headerRef} checked={allSelected}
                                disabled={busy} onChange={togglePage} aria-label="Select all visible rows" /></th>
                            {COLUMNS.map(column => <th key={column.key} className="th--sortable" onClick={() => sort(column.key)}
                                aria-sort={sortColumn === column.key ? (sortOrder === "asc" ? "ascending" : "descending") : "none"}>
                                {column.label} <SortIcon sortOrder={sortColumn === column.key ? sortOrder : null} />
                            </th>)}
                            <th>Actions</th>
                        </tr></thead>
                        <tbody>
                            {!paged.length && <tr><td colSpan={9} className="table-responsive-nodata-text">No Results</td></tr>}
                            {paged.map(site => <tr key={site.id}>
                                <td className="multiselect-input"><input type="checkbox" checked={selectedIds.has(site.id)}
                                    disabled={busy} onChange={() => toggle(site)} aria-label={`Select ${site.siteIdentifier || site.siteName || site.id}`} /></td>
                                <td>{site.siteIdentifier}</td><td>{site.siteName}</td><td>{site.region}</td>
                                <td>{site.systemTypeName}</td><td title={site.statusDescription}>{site.status}</td>
                                <td>{formatDateTime(site.createdAt)}</td><td>{formatDateTime(getPendingExpiry(site))}</td>
                                <td><ActionList disabled={busy || !(site.pendingSiteRequestGuid || site.siteGuid)} customActions={[
                                    { label: "Approve", onClick: () => onAction(site, "approve"), className: "actions__link--success",
                                        itemClassName: "actions__item--approve", disabled: busy || !(site.pendingSiteRequestGuid || site.siteGuid) },
                                    { label: "Reject", onClick: () => onAction(site, "reject"), className: "actions__link--danger",
                                        itemClassName: "actions__item--reject", disabled: busy || !(site.pendingSiteRequestGuid || site.siteGuid) },
                                ]} /></td>
                            </tr>)}
                        </tbody>
                    </table>
                )}
            </div>
            {!loading && sites.length > PAGE_SIZE && <div className="mt-2">
                <Pagination currentPage={page} totalPages={totalPages} onPageChange={setCurrentPage} />
            </div>}
        </>
    );
}
